import React, { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import BackToCatalog from '@/components/Base/BackToCatalog';
import CompanyInfo from '@/components/Base/CompanyInfo';
import PageHelmet from '@/components/Base/PageHelmet';
import Tooltip from '@/components/Base/Tooltip/Tooltip';
import ProductAdvantages from '@/components/Pages/Home/ProductActions/ProductActions';
import {
  CURRENCIES,
  CURRENCY_EUR,
  CURRENCY_UAH,
  CURRENCY_USD,
} from '@/constants';
import { useSeoMeta } from '@/hooks';
import { colorBlack200 } from '@/styleUtils';
import { convertTermMonthsToDays, formatCompactParts } from '@/utils';

import {
  Block,
  ProductDetail,
  ProductDetailCaption,
  ProductDetails,
  ProductDetailValue,
  ProductTitle,
} from './MainInfo.styles';

// eslint-disable-next-line react/require-default-props
export function MainInfo(props) {
  const {
    companyNameG = '',
    companyName = '',
    productName = '',
    companySlug = '',
    bankProductUrl = '',
    companyLicense = '',
    companyRating = '',
    logoUrl = '',
    advantages = [],
    loanLimitNumberMax = null,
    currency = '',
    termMonths = {},
    rateDisplay = {},
    loanLimitNumber = {},
    loanLimitNumberMaxUah = null,
    loanLimitNumberMin = 0,
    loanLimitNumberMinUah = 0,
    currencyRate = {},
    oneTimeCommissionPercent = null,
    monthlyCommissionPercent = null,
  } = props;

  const { locale, formatMessage } = useIntl();
  const { pageTitle, metaTitle, metaDescription } = useSeoMeta({
    title: formatMessage(
      { id: 'productCard.title' },
      { productName, companyNameG }
    ),
  });

  const schema = useMemo(
    () => [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'LoanOrCredit',
          name: pageTitle,
          description: metaDescription,
          loanType: 'Unsecured Personal Loan',
          amount: {
            '@type': 'MonetaryAmount',
            name: formatMessage({ id: 'shema.amount.name' }),
            minValue: loanLimitNumber?.min,
            maxValue: loanLimitNumber?.max,
            currency,
          },
          interestRate: monthlyCommissionPercent,
          annualPercentageRate: rateDisplay?.rate || 0,
          loanTerm: {
            '@type': 'QuantitativeValue',
            ...convertTermMonthsToDays(termMonths),
            unitText: 'days',
          },
          feesAndCommissionsSpecification: oneTimeCommissionPercent,
          provider: {
            '@type': 'BankOrCreditUnion',
            '@id': `https://minfin.com.ua/ua/company/${companySlug}/#bank`,
            name: companyName,
            url: `https://minfin.com.ua/ua/company/${companySlug}/`,
            address: { '@type': 'PostalAddress', addressCountry: 'UA' },
          },
        }),
      },
    ],
    [formatMessage, pageTitle, metaDescription, props]
  );

  return (
    <Block>
      <PageHelmet
        title={metaTitle}
        description={metaDescription}
        script={schema}
      />
      <BackToCatalog />

      <ProductTitle>{pageTitle || null}</ProductTitle>

      <CompanyInfo
        companySlug={companySlug}
        bankProductUrl={bankProductUrl}
        companyName={companyName}
        companyLicense={companyLicense}
        companyRating={companyRating}
        logoUrl={logoUrl}
        isFlatContent
      />

      {!!advantages?.length && <ProductAdvantages advantages={advantages} />}

      <ProductDetails>
        {(!!loanLimitNumberMax || !!loanLimitNumberMin) && (
          <ProductDetail>
            <ProductDetailValue>
              {formatMessage(
                { id: 'productCard.loanLimit' },
                {
                  ...formatCompactParts(
                    loanLimitNumberMax || loanLimitNumberMin,
                    locale
                  ),
                  currency: CURRENCIES[currency],
                  from: loanLimitNumberMin && !loanLimitNumberMax,
                }
              )}

              {(!!loanLimitNumberMaxUah || !!loanLimitNumberMinUah) && (
                <Tooltip
                  contentTooltip={formatMessage(
                    { id: 'tooltip.loanLimitNumber' },
                    {
                      ...formatCompactParts(
                        loanLimitNumberMaxUah || loanLimitNumberMinUah,
                        locale
                      ),
                      currency: CURRENCIES.uah,
                      rate: (
                        <span className={`trend${currencyRate?.trend}`}>
                          {currencyRate?.rate}
                        </span>
                      ),
                    }
                  )}
                  topIconStyle="0"
                  icon="info"
                  textColor={colorBlack200}
                />
              )}
            </ProductDetailValue>

            <ProductDetailCaption>
              {formatMessage({ id: 'productCard.loanLimit.label' })}
            </ProductDetailCaption>
          </ProductDetail>
        )}

        {!!rateDisplay?.text && (
          <ProductDetail>
            <Tooltip
              contentLabel={
                <ProductDetailValue>{rateDisplay.text}</ProductDetailValue>
              }
              contentTooltip={rateDisplay.tooltip}
              topIconStyle="0"
              icon="info"
              textColor={colorBlack200}
            />

            <ProductDetailCaption>
              {formatMessage({ id: 'productCard.loanRateYear.label' })}
            </ProductDetailCaption>
          </ProductDetail>
        )}

        {!!termMonths?.max && (
          <ProductDetail>
            <ProductDetailValue>
              {formatMessage(
                { id: 'productCard.termMonths' },
                {
                  numberPart: termMonths.max,
                }
              )}
            </ProductDetailValue>

            <ProductDetailCaption>
              {formatMessage({ id: 'productCard.termMonths.label' })}
            </ProductDetailCaption>
          </ProductDetail>
        )}
      </ProductDetails>
    </Block>
  );
}

MainInfo.propTypes = {
  loanLimitNumberMax: PropTypes.number,
  loanLimitNumberMin: PropTypes.number,
  loanLimitNumberMinUah: PropTypes.number,
  currency: PropTypes.oneOf([CURRENCY_UAH, CURRENCY_USD, CURRENCY_EUR]),
  termMonths: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
  }),
  advantages: PropTypes.arrayOf(
    PropTypes.shape({ title: PropTypes.string, description: PropTypes.string })
      .isRequired
  ),
  productName: PropTypes.string,
  companyNameG: PropTypes.string,
  companyName: PropTypes.string,
  companySlug: PropTypes.string,
  bankProductUrl: PropTypes.string,
  companyLicense: PropTypes.string,
  companyRating: PropTypes.string,
  logoUrl: PropTypes.string,
  rateDisplay: PropTypes.objectOf(PropTypes.any),
  loanLimitNumber: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
  }),
  loanLimitNumberMaxUah: PropTypes.number,
  currencyRate: PropTypes.shape({
    rate: PropTypes.number,
    trend: PropTypes.string,
  }),
  oneTimeCommissionPercent: PropTypes.number,
  monthlyCommissionPercent: PropTypes.number,
};

export default memo(MainInfo);
